import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { practiceService } from '@/core/services/practice';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import type {
  JournalScreenQuery,
  JournalScreenRow,
  JournalScreenSummary,
  JournalStatus,
  JournalTypeOption,
  ReviewStatus,
} from '@/core/models/phase1.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { Select } from '@/kit/Select';
import { ExportMenu, SearchField, fmt2, periodDash, useAlive, useClientInit, useLive } from './common';
import { JournalDialog, type JournalDialogData } from './JournalDialog';
import {
  ReverseJournalDialog,
  type ReverseJournalChoice,
  type ReverseJournalDialogData,
} from './ReverseJournalDialog';
import { reversalLinkParts } from './reversalLinks';

const ALL = -1;

const STATUS_OPTIONS = [
  { id: ALL, name: 'All' },
  { id: 0, name: 'Draft' },
  { id: 1, name: 'Posted' },
  { id: 2, name: 'Reversed' },
  { id: 3, name: 'Reversal' },
];

const REVIEW_OPTIONS = [
  { id: ALL, name: 'All' },
  { id: 0, name: 'Not reviewed' },
  { id: 1, name: 'Reviewed' },
  { id: 2, name: 'Queried' },
];

interface Model {
  dataSource: JournalScreenRow[];
  periods: PeriodRow[];
  selectedPeriodId: string | null;
  types: JournalTypeOption[];
  summary: JournalScreenSummary | null;
  searchText: string;
  statusFilter: number;
  typeFilter: string | null;
  reviewFilter: number;
}

const statusName = (status: JournalStatus) =>
  status === 0 ? 'Draft' : status === 1 ? 'Posted' : status === 2 ? 'Reversed' : 'Reversal';

/**
 * 'Reversal of #n' / 'Reversed by #m' under the status; the API may not send the numbers yet, so each part is optional. A reversal posted in the
 * NEXT period (APT336) names that period: 'Reversed by #1 in 01-Apr-2026 - 31-Mar-2027'.
 */
const linkText = (row: JournalScreenRow) => reversalLinkParts(row).join(' · ');

const reviewName = (status: ReviewStatus) => (status === 0 ? 'Not reviewed' : status === 1 ? 'Reviewed' : 'Queried');

/**
 * The row's direct actions: Reverse (a Posted journal only - a Reversal cannot itself be reversed, the API refuses
 * it with 'This journal is itself a reversal. To re-instate the original, post a new journal.') and the three review states.
 */
function RowMenu({
  row,
  onReverse,
  onReview,
}: {
  row: JournalScreenRow;
  onReverse: (row: JournalScreenRow) => void;
  onReview: (row: JournalScreenRow, reviewStatus: ReviewStatus) => void;
}) {
  return (
    <>
      {row.status === 1 && (
        <Button title="Reverse" onClick={() => onReverse(row)}>
          <Icon name="undo" outlined />
        </Button>
      )}
      <Button title="Mark reviewed" onClick={() => onReview(row, 1)}>
        <Icon name="done_all" outlined />
      </Button>
      <Button title="Raise query" onClick={() => onReview(row, 2)}>
        <Icon name="help_outline" outlined />
      </Button>
      <Button title="Clear review" onClick={() => onReview(row, 0)}>
        <Icon name="remove_done" outlined />
      </Button>
    </>
  );
}

/**
 * AP-006 journals: list, create/edit draft, post, reverse, delete draft and review status, with the journal type
 * catalogue (including the group-only and elimination flags) and comparatives. A posted journal is never edited or
 * deleted here - it is reversed, which is what the accounting record requires. (Angular TaxreturnJournalsComponent.)
 */
export function TaxreturnJournals() {
  const alive = useAlive();
  const [m, set, ref] = useLive<Model>({
    dataSource: [],
    periods: [],
    selectedPeriodId: null,
    types: [],
    summary: null,
    searchText: '',
    statusFilter: ALL,
    typeFilter: null,
    reviewFilter: ALL,
  });

  const query = (): JournalScreenQuery => {
    const s = ref.current;
    return {
      status: s.statusFilter === ALL ? null : (s.statusFilter as JournalStatus),
      typeCode: s.typeFilter,
      reviewStatus: s.reviewFilter === ALL ? null : (s.reviewFilter as ReviewStatus),
      groupOnly: null,
      search: s.searchText?.trim() || null,
      pageNumber: 1,
      pageSize: 500,
    };
  };

  const load = async () => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      const page = await phase1Service.getJournals(periodId, query());
      if (!alive.current) return;
      set({ dataSource: page.items ?? [], summary: page.summary });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const loadPeriods = async (clientId: string) => {
    try {
      const response = await practiceService.getClientPeriods(clientId);
      if (!alive.current) return;
      const periods = response.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const selectedPeriodId = await resolveDefaultPeriodId(clientId, periods);
      set({ periods, selectedPeriodId });
      if (selectedPeriodId) {
        await load();
      } else {
        notifyError('Add an accounting period before posting journals.');
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const clientRef = useClientInit((client) => {
    phase1Service
      .getJournalTypes()
      .then((types) => {
        // APT272: a response that is present but not an array (an error-shaped body returned with a 200, a
        // not-yet-configured feature, etc.) used to be stored as-is. The type filter Select then ran `items.forEach`
        // over it on the very next render and threw with no error boundary to catch it, blanking the whole screen -
        // which, caught at the wrong moment, could look indistinguishable from "the Add Journal button did nothing".
        if (alive.current) set({ types: Array.isArray(types) ? types : [] });
      })
      .catch((error) => {
        if (alive.current) notifyError(error);
      });

    void loadPeriods(client.id);
  });

  /** Consolidation and elimination types are posted from the group accounts, not here. */
  const entityTypes = () => (ref.current.types ?? []).filter((t) => !t.isGroupOnly);

  const openEditor = (row: JournalScreenRow | null) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }

    // APT272: the "+ Add Journal" button sat inside no try/catch, so any exception while building the dialog's
    // data (e.g. an unexpected shape from a period or type the server returned) threw inside the native click
    // handler. React does not route an event-handler exception through any error boundary, so it never reached
    // notifyError - the click just did nothing, with no dialog, no navigation and nothing in the UI to explain why.
    try {
      const period = ref.current.periods.find((p) => p.id === periodId);
      const data: JournalDialogData = {
        periodId,
        // ap5-a6: the dialog needs the client so the nominal picker can offer the whole chart.
        clientId: clientRef.current?.id ?? '',
        journalId: row ? row.journalId : null,
        readOnly: row ? row.status !== 0 : false,
        types: entityTypes(),
        defaultDate: period?.periodTo ?? null,
      };

      void openDialog(JournalDialog, {
        disableClose: true,
        panelClass: 'cst-invite',
        // wide enough for the line grid (nominal, balances, debit, credit, narrative) without a sideways scroll
        width: '1080px',
        maxWidth: '94vw',
        data,
      })
        .afterClosed()
        .then((saved) => {
          if (saved) {
            void load();
          }
        });
    } catch (error) {
      notifyError(error);
    }
  };

  const onPost = async (row: JournalScreenRow) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }

    if (row.status !== 0) {
      return;
    }
    try {
      await phase1Service.postJournal(periodId, row.journalId, row.eTag);
      if (!alive.current) return;
      notifySuccess('Journal posted.');
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /**
   * Reverses a posted journal with a linked entry. APT336 (the client's answer): the user chooses where - this period (dated at the period end,
   * as before) or the next accounting period (dated at its first day; it then affects only the next year's trial balance). When the period is
   * FINALISED the dialog asks the user to approve posting the reversal on the next period's first day (APT336, the client's answer).
   */
  const onReverse = async (row: JournalScreenRow) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId || row.status !== 1) {
      return;
    }
    const period = ref.current.periods.find((p) => p.id === periodId);
    const periodEnd = period?.periodTo ?? row.journalDate;

    const choice = await openDialog<ReverseJournalDialogData, ReverseJournalChoice>(ReverseJournalDialog, {
      panelClass: 'cst-invite',
      width: '560px',
      maxWidth: '94vw',
      data: { journalNo: row.journalNo, periodId, periodEnd },
    }).afterClosed();
    if (!choice || !alive.current) {
      return;
    }

    try {
      if (choice.targetPeriodId && choice.confirmFinalisedReversal) {
        // A finalised period's journal: the user approved posting the reversal on the next period's first day.
        await phase1Service.reverseJournal(periodId, row.journalId, choice.reversalDate, null, row.eTag, choice.targetPeriodId, true);
      } else if (choice.targetPeriodId) {
        await phase1Service.reverseJournal(periodId, row.journalId, choice.reversalDate, null, row.eTag, choice.targetPeriodId);
      } else {
        await phase1Service.reverseJournal(periodId, row.journalId, choice.reversalDate, null, row.eTag);
      }
      if (!alive.current) return;
      notifySuccess(choice.targetPeriodId ? 'Journal reversed in the next period.' : 'Journal reversed.');
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const onDelete = (row: JournalScreenRow) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }

    if (row.status !== 0) {
      return;
    }
    void openDialog(ClosePopup, { data: { itemName: 'Journal', count: 1 } })
      .afterClosed()
      .then(async (confirmed) => {
        if (!confirmed) {
          return;
        }
        try {
          await phase1Service.deleteJournal(ref.current.selectedPeriodId!, row.journalId);
          if (!alive.current) return;
          notifySuccess('Draft journal deleted.');
          void load();
        } catch (error) {
          if (alive.current) notifyError(error);
        }
      });
  };

  const onReview = async (row: JournalScreenRow, reviewStatus: ReviewStatus) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      return;
    }
    try {
      await phase1Service.setJournalReview(periodId, row.journalId, { reviewStatus, expectedETag: row.eTag });
      if (!alive.current) return;
      notifySuccess('Review status updated.');
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** format: 1 CSV, 2 Excel, 3 PDF. */
  const onExport = async (format: number) => {
    const periodId = ref.current.selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    try {
      await phase1Service.exportJournals(periodId, query(), { format, isPrint: false });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const columns: DataTableColumn<JournalScreenRow>[] = [
    {
      id: 'no',
      header: 'No.',
      cell: (element) => (
        <a className="link" onClick={() => openEditor(element)}>
          {element.journalNo}
        </a>
      ),
    },
    { id: 'date', header: 'Date', cell: (element) => element.journalDate },
    {
      id: 'type',
      header: 'Type',
      cell: (element) => (
        <>
          {element.typeName} {element.isElimination && <span className="text-blue">(elimination)</span>}
          {element.isGroupOnly && !element.isElimination && <span className="text-blue">(group only)</span>}
        </>
      ),
    },
    { id: 'narrative', header: 'Narrative', cell: (element) => element.narrative },
    { id: 'debit', header: 'Debit', cell: (element) => fmt2(element.totalDebit) },
    { id: 'credit', header: 'Credit', cell: (element) => fmt2(element.totalCredit) },
    { id: 'review', header: 'Review', cell: (element) => reviewName(element.reviewStatus) },
    {
      id: 'status',
      header: 'Status',
      cell: (element) => (
        <div>
          <span className={element.status === 0 ? 'text-blue' : 'text-green'}>{statusName(element.status)}</span>
          {/* APT368 (client): "Reversal of #6" / "Reversed by #7" is one small info icon beside the status, not text repeated on every row. */}
          {linkText(element) && (
            <span className="ml-1" data-testid="journal-reversal-link">
              <InfoHint text={linkText(element)} placement="right" />
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          {element.status === 0 && (
            <Button title="Post" onClick={() => void onPost(element)}>
              <Icon name="check_circle" outlined />
            </Button>
          )}
          {element.status === 0 && (
            <Button title="Delete draft" onClick={() => onDelete(element)}>
              <Icon name="delete_outline" outlined />
            </Button>
          )}
          <RowMenu
            row={element}
            onReverse={(row) => void onReverse(row)}
            onReview={(row, status) => void onReview(row, status)}
          />
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="task">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={() => openEditor(null)}>
              <Icon name="add" />
              <span>Add Journal</span>
            </Button>

            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Accounting period"
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              value={m.selectedPeriodId}
              onChange={(value) => {
                set({ selectedPeriodId: (value as string | null) ?? null });
                rememberChosenPeriod(value as string | null);
                void load();
              }}
              renderLabel={periodDash}
              renderOption={periodDash}
            />

            <SearchField
              value={m.searchText}
              onValueChange={(searchText) => set({ searchText })}
              onCommit={() => void load()}
            />
          </div>

          <div className="content-header-right mb-1">
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="All types"
              items={m.types}
              bindLabel="name"
              bindValue="typeCode"
              value={m.typeFilter}
              onChange={(value) => {
                set({ typeFilter: (value as string | null) ?? null });
                void load();
              }}
            />
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={STATUS_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.statusFilter}
              onChange={(value) => {
                set({ statusFilter: value as number });
                void load();
              }}
            />
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={REVIEW_OPTIONS}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.reviewFilter}
              onChange={(value) => {
                set({ reviewFilter: value as number });
                void load();
              }}
            />
            <ExportMenu onExport={(format) => void onExport(format)} />
          </div>
        </div>

        {m.summary && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <span>
                {m.summary.draftCount} draft &middot; {m.summary.postedCount} posted
              </span>
              {m.summary.unreviewedCount > 0 && (
                <span className="text-red">&nbsp;&middot;&nbsp;{m.summary.unreviewedCount} awaiting review</span>
              )}
              <span>&nbsp;&middot;&nbsp;Gross adjustments {fmt2(m.summary.netAdjustment)}</span>
              <span>&nbsp;&middot;&nbsp;Prior year {fmt2(m.summary.priorPeriodAdjustment)}</span>
            </div>
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable columns={columns} rows={m.dataSource} rowKey={(row) => row.journalId} stickyHeader />
        </div>
      </div>
    </SideMenuPage>
  );
}
